import React from 'react'
import styled from 'styled-components'

const TooltipContainer = styled.div`
  min-width: 160px;
  max-width: 224px;
  background: #ffffff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

const ListingImage = styled.img`
  width: 100%;
  height: 64px;
  object-fit: cover;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 64px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 10px;
`

const TooltipContent = styled.div`
  padding: 8px 10px;
`

const ListingPrice = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0294df;
  margin-bottom: 5px;
`

const ListingDetails = styled.div`
  font-size: 10px;
  color: #6b6b6b;
  line-height: 1.4;
  margin-bottom: 5px;

  div {
    margin-bottom: 2px;
  }
`

const BadgeContainer = styled.div`
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  margin-top: 5px;
`

const PropertyType = styled.span`
  display: inline-block;
  padding: 2px 5px;
  background: rgba(2, 148, 223, 0.1);
  color: #0294df;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 500;
`

const DealType = styled.span`
  display: inline-block;
  padding: 2px 5px;
  background: rgba(0, 179, 65, 0.1);
  color: #00b341;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 500;
`

interface ListingMarkerTooltipTranslations {
  rooms: (count: number) => string
  priceOnRequest: string
  noImage: string
  forRent: string
  forSale: string
}

const defaultTranslations: ListingMarkerTooltipTranslations = {
  rooms: (count: number) => `${count} ${count === 1 ? 'room' : 'rooms'}`,
  priceOnRequest: 'Price on request',
  noImage: 'No image',
  forRent: 'For Rent',
  forSale: 'For Sale',
}

interface ListingMarkerTooltipProps {
  listing: {
    id: number
    price: number
    priceLabel?: string
    propertyType?: string
    dealType?: string
    rooms?: number
    area?: number
    city?: string
    title?: string
    urls_image?: string
    hash_images?: string
  }
  translations?: Partial<ListingMarkerTooltipTranslations>
}

/**
 * Tooltip component displayed when hovering over listing markers on the map
 */
export const ListingMarkerTooltip: React.FC<ListingMarkerTooltipProps> = ({
  listing,
  translations,
}) => {
  const t = { ...defaultTranslations, ...translations }
  // Format full price for display
  const formatPrice = (price: number) => {
    if (!price) return t.priceOnRequest
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  // Get image URL
  const imageUrl = listing.urls_image
    ? listing.urls_image
    : listing.hash_images
      ? `https://images.popety.io/${listing.hash_images}`
      : null

  return (
    <TooltipContainer>
      {imageUrl ? (
        <ListingImage src={imageUrl} alt={listing.title || `Listing ${listing.id}`} />
      ) : (
        <ImagePlaceholder>{t.noImage}</ImagePlaceholder>
      )}

      <TooltipContent>
        <ListingPrice>{formatPrice(listing.price)}</ListingPrice>

        <ListingDetails>
          {listing.rooms && listing.area && (
            <div>
              {t.rooms(listing.rooms)} • {listing.area} m²
            </div>
          )}
          {!listing.rooms && listing.area && <div>{listing.area} m²</div>}
          {listing.rooms && !listing.area && <div>{t.rooms(listing.rooms)}</div>}
          {listing.city && <div>{listing.city}</div>}
        </ListingDetails>

        <BadgeContainer>
          {listing.propertyType && <PropertyType>{listing.propertyType}</PropertyType>}
          {listing.dealType && (
            <DealType>{listing.dealType === 'rent' ? t.forRent : t.forSale}</DealType>
          )}
        </BadgeContainer>
      </TooltipContent>
    </TooltipContainer>
  )
}
